import * as React from "react";
import { useState } from "react";
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, MenuItem } from "@mui/material";
import { useDispatch } from "react-redux";
import CloseIcon from "@mui/icons-material/Close";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { useSaveFieldAdminVehicleMutation, useSetFieldAdminVehicleStatusMutation } from "./fieldAdminApi";
import { AMBER, BORDER } from "../../Global/colors";
import { showSuccess } from "../../Global/notifications/notificationsSlice";
import { FAGhostButton, FAPrimaryButton } from "./FAControls";
import { FAFieldError } from "./FAStates";
import { FATextField, FASelect } from "./StockFormShell";
import { FA_DIALOG_PAPER_SX } from "./FALayout";
import { teamLabel, WARN_YELLOW } from "./designTokens";

// Fleet's add/edit form and its retire confirmation. Both are standalone — their own
// state, their own mutations — and Fleet.js was 443 lines with them inline.

const FUEL_OPTIONS = ["Diesel", "Petrol", "Hybrid", "Electric"];

export function VehicleDialog({ vehicle, vehicles, teamId, teamName, onClose }) {
  const dispatch = useDispatch();
  const isEdit = Boolean(vehicle.id);
  const [saveVehicle, { isLoading, error }] = useSaveFieldAdminVehicleMutation();

  const [makeModel, setMakeModel] = useState(vehicle.make_model || "");
  const [registration, setRegistration] = useState(vehicle.registration || "");
  const [year, setYear] = useState(vehicle.year ?? "");
  const [fuel, setFuel] = useState(vehicle.fuel || "Diesel");
  const [odometerKm, setOdometerKm] = useState(vehicle.odometer_km ?? "");
  const [vehicleStatus, setVehicleStatus] = useState(vehicle.status || "active");
  const [assigned, setAssigned] = useState(vehicle.assigned_team_id === teamId);
  const [lastServiced, setLastServiced] = useState(vehicle.last_serviced || "");
  const [serviceDueInKm, setServiceDueInKm] = useState(vehicle.service_due_in_km ?? "");

  // With no Team model, assigned_team_id can only ever equal this FA's own team_id, so
  // assigning any vehicle silently releases whatever else was on it (VehicleView.post's
  // one-per-team rule). Warn before that happens rather than let it look like a bug.
  const currentlyAssigned = vehicles.find((v) => v.id !== vehicle.id && v.assigned_team_id === teamId);
  const willRelease = assigned && !vehicle.assigned_team_id && currentlyAssigned;

  const disabled = !makeModel.trim() || !registration.trim() || isLoading;

  const handleSave = async () => {
    const body = {
      id: vehicle.id || undefined,
      make_model: makeModel.trim(),
      registration: registration.trim(),
      year: year === "" ? null : Number(year),
      fuel,
      odometer_km: odometerKm === "" ? null : Number(odometerKm),
      status: vehicleStatus,
      assigned_team_id: assigned ? teamId : null,
      last_serviced: lastServiced || null,
      service_due_in_km: serviceDueInKm === "" ? null : Number(serviceDueInKm),
    };
    const result = await saveVehicle(body);
    if (!result.error) {
      dispatch(showSuccess(vehicle.id ? "Vehicle updated" : "Vehicle added"));
      onClose();
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" sx={FA_DIALOG_PAPER_SX}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
        <LocalShippingOutlinedIcon sx={{ color: AMBER, fontSize: "1.15rem" }} />
        <Box sx={{ flex: 1, fontSize: "1.05rem", fontWeight: 700 }}>{isEdit ? "Edit vehicle" : "Add vehicle"}</Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "rgba(255,255,255,0.45)" }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: BORDER }}>
        <Box sx={{ mb: "14px" }}>
          <FATextField label="Make & model" value={makeModel} onChange={(e) => setMakeModel(e.target.value)} placeholder="e.g. Toyota Hilux DC" autoFocus />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px", mb: "14px" }}>
          <FATextField label="Registration" value={registration} onChange={(e) => setRegistration(e.target.value)} placeholder="e.g. BBD 421 GP" />
          <FATextField label="Year" type="number" value={year} onChange={(e) => setYear(e.target.value)} placeholder="e.g. 2022" />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px", mb: "14px" }}>
          <FASelect label="Fuel" value={fuel} onChange={(e) => setFuel(e.target.value)}>
            {FUEL_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </FASelect>
          <FATextField label="Odometer (km)" type="number" value={odometerKm} onChange={(e) => setOdometerKm(e.target.value)} placeholder="km" />
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px", mb: vehicleStatus === "retired" ? "4px" : "14px" }}>
          {/* Retired is deliberately not a selectable option here — retire/restore only
              happen via the row-action menu, behind its own confirm, so there's exactly
              one path to that destructive state, not two. */}
          <FASelect label="Status" value={vehicleStatus} onChange={(e) => setVehicleStatus(e.target.value)} disabled={vehicle.status === "retired"}>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="in_service">In service (off-road)</MenuItem>
            {vehicle.status === "retired" ? <MenuItem value="retired">Retired</MenuItem> : null}
          </FASelect>
          <FASelect label="Assigned team" value={assigned ? "mine" : ""} onChange={(e) => setAssigned(e.target.value === "mine")} disabled={vehicle.status === "retired"}>
            <MenuItem value="">Unassigned</MenuItem>
            <MenuItem value="mine">{teamLabel(teamName, teamId)} (my team)</MenuItem>
          </FASelect>
        </Box>
        {vehicle.status === "retired" ? (
          <Typography sx={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.45)", mb: "14px" }}>
            Restore this vehicle from its row menu to change its status.
          </Typography>
        ) : null}
        {willRelease ? (
          <Typography sx={{ fontSize: "0.76rem", color: WARN_YELLOW, backgroundColor: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: "8px", p: "9px 11px", mb: "14px" }}>
            {teamLabel(teamName, teamId)} currently has {currentlyAssigned.make_model} · {currentlyAssigned.registration}. Saving will release it.
          </Typography>
        ) : null}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px" }}>
          <FATextField label="Last serviced" type="date" value={lastServiced} onChange={(e) => setLastServiced(e.target.value)} InputLabelProps={{ shrink: true }} />
          <FATextField label="Service due (km remaining)" type="number" value={serviceDueInKm} onChange={(e) => setServiceDueInKm(e.target.value)} placeholder="km to next service" />
        </Box>

        <FAFieldError error={error} />
      </DialogContent>
      <DialogActions sx={{ p: "14px 20px" }}>
        <FAGhostButton onClick={onClose}>Cancel</FAGhostButton>
        <Box sx={{ flex: 1 }} />
        <FAPrimaryButton
          onClick={handleSave}
          disabled={disabled}
        >
          {isLoading ? "Saving…" : "Save vehicle"}
        </FAPrimaryButton>
      </DialogActions>
    </Dialog>
  );
}

export function RetireConfirmDialog({ vehicle, onClose }) {
  const dispatch = useDispatch();
  const [setStatus, { isLoading }] = useSetFieldAdminVehicleStatusMutation();

  const handleConfirm = async () => {
    const result = await setStatus({ vehicle_id: vehicle.id, status: "retired" });
    if (!result.error) {
      dispatch(showSuccess("Vehicle retired"));
      onClose();
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth sx={FA_DIALOG_PAPER_SX}>
      <DialogTitle sx={{ fontSize: "1.05rem", fontWeight: 700 }}>Retire this vehicle?</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: "0.86rem", color: "rgba(255,255,255,0.7)", lineHeight: 1.5 }}>
          Retiring {vehicle.make_model} · {vehicle.registration} removes it from the fleet and any team assignment. It stays on record and can be restored. No hard delete.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: "14px 20px" }}>
        <FAGhostButton onClick={onClose}>Cancel</FAGhostButton>
        <Box sx={{ flex: 1 }} />
        <FAPrimaryButton danger onClick={handleConfirm} disabled={isLoading}>
          {isLoading ? "Retiring…" : "Retire vehicle"}
        </FAPrimaryButton>
      </DialogActions>
    </Dialog>
  );
}
